import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';

export type GetPhoneNumbersFn = (params: {
  adId?: string;
  shopId?: string;
  isFloatingButtonClicked?: boolean;
  triggerElement?: HTMLElement;
}) => void;

export interface UseShopsPhonesControllerParams {
  isDesktop: boolean;
  phoneNumbers: string[];
  getPhoneNumbersOriginal: GetPhoneNumbersFn;
}

export interface ShopPhonesPresentation {
  singlePhone: string | null;
  popupPhones: string[];
  onShowNumberClick: (e: ReactMouseEvent<HTMLElement>, shopId: string) => void;
  onClosePopup: () => void;
  onPhoneClick: () => void;
}

export function useShopsPhonesController({
  isDesktop,
  phoneNumbers,
  getPhoneNumbersOriginal,
}: UseShopsPhonesControllerParams) {
  const [activeShopId, setActiveShopId] = useState<string | null>(null);
  const [requestedShopId, setRequestedShopId] = useState<string | null>(null);
  const [displayPhoneNumbers, setDisplayPhoneNumbers] = useState<string[]>([]);
  const requestBaseNumbersRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (!requestedShopId || requestedShopId !== activeShopId) return;
    if (!requestBaseNumbersRef.current) return;
    if (phoneNumbers === requestBaseNumbersRef.current) return;
    setDisplayPhoneNumbers(phoneNumbers);
    requestBaseNumbersRef.current = null;
  }, [phoneNumbers, requestedShopId, activeShopId]);

  const closePopup = useCallback(() => {
    setActiveShopId(null);
    setRequestedShopId(null);
    setDisplayPhoneNumbers([]);
    requestBaseNumbersRef.current = null;
  }, []);

  const getPhoneNumbers = useCallback(
    (params: Parameters<GetPhoneNumbersFn>[0]) => {
      if (params.shopId) {
        setActiveShopId(params.shopId);
        setRequestedShopId(params.shopId);
        setDisplayPhoneNumbers([]);
        requestBaseNumbersRef.current = phoneNumbers;
      }
      getPhoneNumbersOriginal(params);
    },
    [getPhoneNumbersOriginal, phoneNumbers]
  );

  const getPresentationForShop = useCallback(
    (shopId: string): ShopPhonesPresentation => {
      const phones = displayPhoneNumbers ?? [];
      const isActive = activeShopId === shopId;
      const showSinglePhone = isActive && phones.length === 1;
      const singlePhone = showSinglePhone ? phones[0] : null;
      const showMultiplePhones = isDesktop && isActive && phones.length > 1;
      const popupPhones = showMultiplePhones ? phones : [];

      return {
        singlePhone,
        popupPhones,
        onShowNumberClick: (e, id) => {
          e.preventDefault();
          if (popupPhones.length > 0) {
            closePopup();
            return;
          }
          getPhoneNumbers({
            shopId: id,
            triggerElement: e.currentTarget,
          });
        },
        onClosePopup: closePopup,
        onPhoneClick: closePopup,
      };
    },
    [activeShopId, closePopup, displayPhoneNumbers, getPhoneNumbers, isDesktop]
  );

  return {
    getPhoneNumbers,
    closePopup,
    getPresentationForShop,
    activeShopId,
    displayPhoneNumbers,
  };
}
